import React, { useState } from 'react';
import NavigationBar from './NavigationBar';
import './MaterialCalculator.css';

interface MaterialData {
  density: number;
  pricePerKg: number;
  unit: string;
}

interface MaterialResult {
  volume: number;
  weight: number;
  cost: number;
}

const materials: { [key: string]: MaterialData } = {
  steel: { density: 7.85, pricePerKg: 5.5, unit: 'g/cm³' },
  aluminum: { density: 2.7, pricePerKg: 18, unit: 'g/cm³' },
  copper: { density: 8.96, pricePerKg: 65, unit: 'g/cm³' },
  titanium: { density: 4.51, pricePerKg: 120, unit: 'g/cm³' },
  plastic: { density: 1.2, pricePerKg: 8, unit: 'g/cm³' },
};

const MaterialCalculator: React.FC = () => {
  const [selectedMaterial, setSelectedMaterial] = useState<string>('steel');
  const [length, setLength] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [shape, setShape] = useState<string>('rectangular');
  const [result, setResult] = useState<MaterialResult | null>(null);

  const calculateMaterial = () => {
    const l = parseFloat(length);
    const w = parseFloat(width);
    const h = parseFloat(height);

    if (l && w && h && l > 0 && w > 0 && h > 0) {
      let volume: number;

      if (shape === 'rectangular') {
        volume = l * w * h;
      } else if (shape === 'cylindrical') {
        volume = Math.PI * Math.pow(w / 2, 2) * l;
      } else {
        volume = (4 / 3) * Math.PI * Math.pow(l / 2, 3);
      }

      const material = materials[selectedMaterial];
      const weight = (volume * material.density) / 1000;
      const cost = weight * material.pricePerKg;

      setResult({
        volume,
        weight,
        cost,
      });
    }
  };

  const getMaterialName = (key: string) => {
    const names: { [key: string]: string } = {
      steel: '钢材',
      aluminum: '铝材',
      copper: '铜材',
      titanium: '钛合金',
      plastic: '塑料',
    };
    return names[key] || key;
  };

  const getShapeName = (key: string) => {
    const names: { [key: string]: string } = {
      rectangular: '长方体',
      cylindrical: '圆柱体',
      spherical: '球体',
    };
    return names[key] || key;
  };

  return (
    <div className="material-calculator">
      <NavigationBar title="材料计算器" />

      <div className="input-group">
        <label>
          材料类型:
          <select
            value={selectedMaterial}
            onChange={e => setSelectedMaterial(e.target.value)}
          >
            {Object.keys(materials).map(key => (
              <option key={key} value={key}>
                {getMaterialName(key)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="input-group">
        <label>
          形状:
          <select value={shape} onChange={e => setShape(e.target.value)}>
            <option value="rectangular">长方体</option>
            <option value="cylindrical">圆柱体</option>
            <option value="spherical">球体</option>
          </select>
        </label>
      </div>

      <div className="input-group">
        <label>
          {shape === 'cylindrical'
            ? '高度 (mm):'
            : shape === 'spherical'
              ? '直径 (mm):'
              : '长度 (mm):'}
          <input
            type="number"
            value={length}
            onChange={e => setLength(e.target.value)}
            placeholder={
              shape === 'cylindrical'
                ? '输入高度'
                : shape === 'spherical'
                  ? '输入直径'
                  : '输入长度'
            }
          />
        </label>
      </div>

      <div className="input-group">
        <label>
          {shape === 'cylindrical'
            ? '直径 (mm):'
            : shape === 'spherical'
              ? ''
              : '宽度 (mm):'}
          {shape !== 'spherical' && (
            <input
              type="number"
              value={width}
              onChange={e => setWidth(e.target.value)}
              placeholder={shape === 'cylindrical' ? '输入直径' : '输入宽度'}
            />
          )}
        </label>
      </div>

      {shape === 'rectangular' && (
        <div className="input-group">
          <label>
            高度 (mm):
            <input
              type="number"
              value={height}
              onChange={e => setHeight(e.target.value)}
              placeholder="输入高度"
            />
          </label>
        </div>
      )}

      <button onClick={calculateMaterial} className="calculate-btn">
        计算材料
      </button>

      {result && (
        <div className="result">
          <h3>计算结果</h3>
          <div className="result-item">
            <span>材料类型:</span>
            <span>{getMaterialName(selectedMaterial)}</span>
          </div>
          <div className="result-item">
            <span>形状:</span>
            <span>{getShapeName(shape)}</span>
          </div>
          <div className="result-item">
            <span>体积:</span>
            <span>{result.volume.toFixed(2)} mm³</span>
          </div>
          <div className="result-item">
            <span>重量:</span>
            <span>{result.weight.toFixed(2)} kg</span>
          </div>
          <div className="result-item">
            <span>估算成本:</span>
            <span>¥{result.cost.toFixed(2)}</span>
          </div>
        </div>
      )}

      <div className="material-info">
        <h4>材料属性</h4>
        <div className="material-grid">
          {Object.entries(materials).map(([key, data]) => (
            <div key={key} className="material-card">
              <h5>{getMaterialName(key)}</h5>
              <p>
                密度: {data.density} {data.unit}
              </p>
              <p>价格: ¥{data.pricePerKg}/kg</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MaterialCalculator;
